// TopicCardRedesigned.js - Redesigned to match sidebar aesthetic
import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  Platform,
  useWindowDimensions,
  Animated,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import DifficultyBadge from "./learning/DifficultyBadge";

// Threshold for compact mode (below this width, use compact styles)
const COMPACT_THRESHOLD = 1536;

// Shimmer placeholder component for loading state
const ShimmerPlaceholder = ({ isDarkMode, style }) => {
  const shimmerAnim = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1500,
        useNativeDriver: false,
      })
    );
    loop.start();
    return () => loop.stop();
  }, [shimmerAnim]);

  const bgBase = isDarkMode ? "#1e293b" : "#e2e8f0";
  const bgShimmer = isDarkMode ? "#334155" : "#f1f5f9";

  const backgroundColor = shimmerAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [bgBase, bgShimmer, bgBase],
  });

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          backgroundColor,
        },
        style,
      ]}
    />
  );
};

// Color tint map for action buttons
const buttonTints = {
  amber: {
    dark: "rgba(245, 158, 11, 0.12)",
    light: "rgba(245, 158, 11, 0.08)",
  },
  green: {
    dark: "rgba(16, 185, 129, 0.12)",
    light: "rgba(16, 185, 129, 0.08)",
  },
  blue: {
    dark: "rgba(59, 130, 246, 0.12)",
    light: "rgba(59, 130, 246, 0.08)",
  },
  purple: {
    dark: "rgba(139, 92, 246, 0.12)",
    light: "rgba(139, 92, 246, 0.08)",
  },
};

/**
 * A single action chip on a topic card (Summary / 3D / Video / Topic Quiz).
 *
 * The chip is narrow, so the label is truncated to one line ("Summa…",
 * "Topic Q…"). On web, hovering the chip shows a tooltip with the full,
 * untruncated label. Kept at module scope (not defined inside the card
 * component) so its hover state survives parent re-renders instead of being
 * remounted and reset on every render.
 */
function ActionButton({
  icon,
  label,
  count,
  onPress,
  color,
  tint,
  isDarkMode,
  isCompact,
  colors,
  dynamicStyles,
}) {
  const [hovered, setHovered] = React.useState(false);

  const hoverProps =
    Platform.OS === "web"
      ? {
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        }
      : {};

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[
        styles.actionButton,
        dynamicStyles.actionButton,
        { backgroundColor: tint ? (isDarkMode ? tint.dark : tint.light) : "transparent" },
        hovered && styles.actionButtonHovered,
      ]}
      activeOpacity={0.7}
      accessibilityLabel={label}
      {...hoverProps}
    >
      {/* Hover tooltip — reveals the full label that the chip truncates */}
      {hovered && Platform.OS === "web" && (
        <View pointerEvents="none" style={styles.actionTooltipWrap}>
          <View
            style={[
              styles.actionTooltipBubble,
              { backgroundColor: isDarkMode ? "#334155" : "#0f172a" },
            ]}
          >
            <Text style={styles.actionTooltipText} numberOfLines={2}>
              {label}
            </Text>
          </View>
        </View>
      )}

      <View style={[styles.actionButtonInner, dynamicStyles.actionButtonInner]}>
        <MaterialCommunityIcons
          name={icon}
          size={isCompact ? 14 : 20}
          color={color || colors.accentPrimary}
        />
        {count !== undefined && (
          <Text style={[styles.actionCount, dynamicStyles.actionCount, { color: colors.textSecondary }]}>
            {count}
          </Text>
        )}
      </View>
      <Text
        style={[styles.actionLabel, dynamicStyles.actionLabel, { color: colors.textMuted }]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
}

/**
 * Redesigned TopicCard that matches the sidebar's refined aesthetic
 * Design language: Indigo accents, slate backgrounds, subtle shadows, accent borders
 */
const TopicCardRedesigned = React.memo(function TopicCardRedesigned({
  item,
  imgUrl,
  swanSrc,
  isDarkMode,
  t,
  onStart,
  onOpenPwo,
  onOpenVideo,
  onOpenQuiz,
}) {
  // Detect if we should use compact mode (zoomed in or small screen)
  const { width } = useWindowDimensions();
  const isCompact = width < COMPACT_THRESHOLD;

  // Color palette matching sidebar
  const colors = {
    cardBg: isDarkMode ? "#0f172a" : "#ffffff",
    cardBgHover: isDarkMode ? "#1e293b" : "#f8fafc",
    border: isDarkMode ? "rgba(148, 163, 184, 0.1)" : "rgba(148, 163, 184, 0.2)",
    accentPrimary: isDarkMode ? "#818cf8" : "#6366f1",
    accentSecondary: isDarkMode ? "#6366f1" : "#818cf8",
    textPrimary: isDarkMode ? "#f1f5f9" : "#0f172a",
    textSecondary: isDarkMode ? "#94a3b8" : "#64748b",
    textMuted: isDarkMode ? "#64748b" : "#94a3b8",
    imageBg: isDarkMode ? "#1e293b" : "#f1f5f9",
    shadow: isDarkMode ? "#000" : "#64748b",
  };

  const hasNet = !!imgUrl;
  const [netReady, setNetReady] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  React.useEffect(() => { setNetReady(false); }, [imgUrl]);

  const fill = { position: "absolute", left: 0, top: 0, width: "100%", height: "100%" };

  const topicLabel = (
    item?.topicDisplayName ||
    item?.topicName ||
    item?.display_name ||
    item?.name ||
    ""
  ).trim();

  // Web-specific hover handlers
  const webHoverProps = Platform.OS === "web" ? {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  } : {};

  // Dynamic styles based on zoom level
  const dynamicStyles = isCompact ? compactStyles : normalStyles;

  return (
    <TouchableOpacity
      testID="topic-card"
      onPress={() => onStart(item)}
      activeOpacity={0.9}
      {...webHoverProps}
      style={[
        styles.card,
        dynamicStyles.card,
        {
          backgroundColor: isHovered ? colors.cardBgHover : colors.cardBg,
          borderColor: isHovered ? colors.accentPrimary : colors.border,
          shadowColor: colors.shadow,
          shadowOpacity: isDarkMode ? 0.4 : 0.1,
          transform: isHovered ? [{ scale: 1.02 }] : [{ scale: 1 }],
        },
      ]}
    >
      {/* Image container */}
      <View style={[styles.imageContainer, dynamicStyles.imageContainer, { backgroundColor: colors.imageBg }]}>
        {/* Gradient overlay for depth */}
        <View style={[styles.imageOverlay, dynamicStyles.imageOverlay]} />

        {/* Shimmer placeholder — shown while image loads */}
        {!(hasNet && netReady) && (
          <ShimmerPlaceholder isDarkMode={isDarkMode} />
        )}

        {/* Network image */}
        {hasNet && (
          <Image
            source={{ uri: imgUrl }}
            style={[fill, { opacity: netReady ? 1 : 0 }]}
            resizeMode="cover"
            onError={() => setNetReady(false)}
            onLoad={(e) => {
              const w = e?.nativeEvent?.source?.width ?? 1;
              const h = e?.nativeEvent?.source?.height ?? 1;
              setNetReady(w > 0 && h > 0);
            }}
          />
        )}

      </View>

      {/* Content body */}
      <View style={[styles.body, dynamicStyles.body]}>
        {/* Title section with fixed height */}
        <View>
          <View style={[styles.titleContainer, dynamicStyles.titleContainer]}>
            <Text
              style={[styles.title, dynamicStyles.title, { color: colors.textPrimary }]}
              numberOfLines={2}
            >
              {topicLabel}
            </Text>
          </View>
          {/* Difficulty badge (renders nothing until backend tags item.difficulty) */}
          <DifficultyBadge
            level={item.difficulty || item.difficulty_level}
            isDarkMode={isDarkMode}
            style={{ marginBottom: 6 }}
          />
          {/* Divider */}
          <View style={[styles.divider, dynamicStyles.divider, { backgroundColor: colors.border }]} />
        </View>

        {/* Action buttons grid */}
        <View style={[styles.actionsRow, dynamicStyles.actionsRow]}>
          <ActionButton
            icon="lightbulb-outline"
            label={t("Summary")}
            onPress={() => onStart(item)}
            color="#f59e0b"
            tint={buttonTints.amber}
            isDarkMode={isDarkMode}
            isCompact={isCompact}
            colors={colors}
            dynamicStyles={dynamicStyles}
          />

          {[
            {
              key: "pwo",
              icon: "cube-outline",
              label: t("3D"),
              count: item.pwoCount,
              onPress: () => onOpenPwo(item),
              color: "#10b981",
              tint: buttonTints.green,
            },
            {
              key: "video",
              icon: "play-circle-outline",
              label: t("Video"),
              count: item.videoCount,
              onPress: () => onOpenVideo(item),
              color: "#3b82f6",
              tint: buttonTints.blue,
            },
            {
              key: "quiz",
              // AIT-251: glossary canon for Quiz is lightbulb-outline + blue, but
              // Summary already uses lightbulb-outline on this same card; we keep
              // help-circle-outline here to avoid icon collision and apply the
              // canonical blue accent via the quizAccent token instead.
              icon: "help-circle-outline",
              label: t("Topic Quiz"),
              count: item.quizCount,
              onPress: () => onOpenQuiz(item),
              color: "#3b82f6",
              tint: buttonTints.blue,
            },
          ]
            .filter(a => (a.count ?? 0) > 0)
            .map(a => (
              <ActionButton
                key={a.key}
                icon={a.icon}
                label={a.label}
                count={a.count ?? 0}
                onPress={a.onPress}
                color={a.color}
                tint={a.tint}
                isDarkMode={isDarkMode}
                isCompact={isCompact}
                colors={colors}
                dynamicStyles={dynamicStyles}
              />
            ))}
        </View>
      </View>
    </TouchableOpacity>
  );
}, (prev, next) => {
  const prevLabel = (
    prev.item?.topicDisplayName ||
    prev.item?.topicName ||
    prev.item?.display_name ||
    prev.item?.name ||
    ""
  ).trim();
  const nextLabel = (
    next.item?.topicDisplayName ||
    next.item?.topicName ||
    next.item?.display_name ||
    next.item?.name ||
    ""
  ).trim();

  return (
    prev.isDarkMode === next.isDarkMode &&
    prev.imgUrl === next.imgUrl &&
    prev.item.topicId === next.item.topicId &&
    prev.item.topicImagePath === next.item.topicImagePath &&
    prev.item.pwoCount === next.item.pwoCount &&
    prev.item.videoCount === next.item.videoCount &&
    prev.item.quizCount === next.item.quizCount &&
    prevLabel === nextLabel
  );
});

// Base styles (shared between normal and compact modes)
const styles = StyleSheet.create({
  card: {
    width: "100%",
    overflow: "hidden",
    borderWidth: 1,
    position: "relative",
    ...(Platform.OS === "web" ? {
      transition: "all 0.2s ease-out",
      cursor: "pointer",
    } : {}),
  },
  imageContainer: {
    position: "relative",
    overflow: "hidden",
  },
  imageOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(to top, rgba(0,0,0,0.3), transparent)",
    zIndex: 1,
  },
  body: {
    flex: 1,
    justifyContent: "space-between",
  },
  titleContainer: {
    overflow: "hidden",
  },
  title: {
    fontWeight: "700",
  },
  divider: {
    height: 1,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  actionButton: {
    flex: 1,
    alignItems: "center",
    // No `overflow: hidden` here — the rounded background is clipped by
    // borderRadius and the label truncates via numberOfLines, so clipping is
    // unnecessary and would cut off the hover tooltip rising above the chip.
    minWidth: 0,
    ...(Platform.OS === "web" ? {
      transition: "background-color 0.15s ease",
    } : {}),
  },
  // Raise the hovered chip above its siblings so its tooltip is not painted
  // over by the chips that come after it in the row.
  actionButtonHovered: {
    zIndex: 20,
  },
  actionButtonInner: {
    flexDirection: "row",
    alignItems: "center",
  },
  // Hover tooltip: a container stretched across the chip width so the bubble
  // can be centered, sitting just above the chip.
  actionTooltipWrap: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    right: 0,
    alignItems: "center",
    marginBottom: 6,
    zIndex: 30,
  },
  actionTooltipBubble: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    maxWidth: 180,
    ...(Platform.OS === "web"
      ? { boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }
      : { elevation: 4 }),
  },
  actionTooltipText: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "600",
    textAlign: "center",
  },
  actionCount: {
    fontWeight: "600",
  },
  actionLabel: {
    fontWeight: "500",
    textTransform: "uppercase",
    textAlign: "center",
    alignSelf: "stretch",
    maxWidth: "100%",
  },
});

// Normal styles (100% zoom)
const normalStyles = StyleSheet.create({
  card: {
    height: 310,
    borderRadius: 12,
    marginBottom: 12,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 12,
    elevation: 3,
  },
  imageContainer: {
    height: 150,
  },
  imageOverlay: {
    height: 40,
  },
  body: {
    padding: 14,
  },
  titleContainer: {
    height: 44,
  },
  title: {
    fontSize: 15.5,
    lineHeight: 20,
    letterSpacing: -0.1,
  },
  divider: {
    marginVertical: 12,
  },
  actionsRow: {
    gap: 8,
  },
  actionButton: {
    paddingVertical: 10,
    paddingHorizontal: 6,
    borderRadius: 8,
  },
  actionButtonInner: {
    gap: 4,
  },
  actionCount: {
    fontSize: 13,
  },
  actionLabel: {
    fontSize: 10,
    marginTop: 4,
    letterSpacing: 0.3,
  },
});

// Compact styles (zoomed in or smaller screens)
const compactStyles = StyleSheet.create({
  card: {
    height: 200,
    borderRadius: 8,
    marginBottom: 6,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2,
  },
  imageContainer: {
    height: 85,
  },
  imageOverlay: {
    height: 20,
  },
  body: {
    padding: 8,
  },
  titleContainer: {
    height: 30,
  },
  title: {
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0,
  },
  divider: {
    marginVertical: 6,
  },
  actionsRow: {
    gap: 2,
  },
  actionButton: {
    paddingVertical: 4,
    paddingHorizontal: 2,
    borderRadius: 4,
  },
  actionButtonInner: {
    gap: 2,
  },
  actionCount: {
    fontSize: 10,
  },
  actionLabel: {
    fontSize: 8,
    marginTop: 1,
    letterSpacing: 0.1,
  },
});

export default TopicCardRedesigned;
